import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import Modal from './Modal.jsx';
import apiClient from '../../services/api/apiClient.js';

/**
 * Formulario de alta/edición dirigido por config — patrón estándar Sistemify.
 * `fields`: `[{ key, label, type, required, options, refRoute, refLabel }]`.
 *   - type: 'text' | 'number' | 'email' | 'textarea' | 'select' | 'date' | 'ref'
 *   - 'select' usa `options: [{value,label}]`
 *   - 'ref' carga opciones reales de `refRoute` (catálogo) y usa `refLabel` (campo a
 *     mostrar, default 'nombre_comercial'/'name')
 *   - 'ref' admite `refFilter: (opcion) => boolean` opcional para restringir las
 *     opciones mostradas (ej. Devoluciones Inversa en V1 —
 *     `DevolucionesInversaCreate.jsx` — solo permite elegir bancos en MXN en el
 *     <select> de Banco; ver `resources.finanzas-cxc.jsx#devoluciones-inversa`).
 */
const COL_SPAN = { 2: 'sm:col-span-2', 3: 'sm:col-span-3', 4: 'sm:col-span-4', 6: 'sm:col-span-6', 12: 'sm:col-span-12' };

export default function FormModal({ open, onClose, title, fields, initial, onSubmit, width, defaults }) {
  const [valores, setValores] = useState({});
  const [refOptions, setRefOptions] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});

  useEffect(() => {
    if (!open) return;
    const base = {};
    fields.forEach((f) => { base[f.key] = initial?.[f.key] ?? defaults?.[f.key] ?? ((f.type === 'select' && !f.placeholder && f.options?.[0]?.value) || ''); });
    setValores(base);
    setErrores({});
  }, [open, initial, fields, defaults]);

  useEffect(() => {
    if (!open) return;
    const refs = fields.filter((f) => f.type === 'ref' && f.refRoute);
    refs.forEach((f) => {
      apiClient.get(`/${f.refRoute}`, { params: { limit: 200, sortBy: f.refLabel || 'nombre_comercial', sortOrder: 'asc' } })
        .then(({ data }) => setRefOptions((prev) => ({ ...prev, [f.key]: data.data || [] })))
        .catch(() => setRefOptions((prev) => ({ ...prev, [f.key]: [] })));
    });
  }, [open, fields]);

  function set(key, value) {
    setValores((v) => ({ ...v, [key]: value }));
  }

  function validar() {
    const nuevos = {};
    fields.forEach((f) => {
      if (f.required && !String(valores[f.key] ?? '').trim()) nuevos[f.key] = 'Requerido';
    });
    setErrores(nuevos);
    return Object.keys(nuevos).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validar()) return;
    setGuardando(true);
    try {
      await onSubmit(valores);
      onClose();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: error.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} width={width}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancelar</button>
          <button type="submit" form="form-modal" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      {/* `col` (opcional, 2..12) en un campo => grilla de 12 columnas como las filas
          `<Col md={n}>` de V1; sin `col` en ningún campo => grilla de 2 columnas. */}
      <form id="form-modal" onSubmit={handleSubmit} className={`grid grid-cols-1 gap-4 ${fields.some((f) => f.col) ? 'sm:grid-cols-12' : 'sm:grid-cols-2'}`}>
        {fields.map((f) => (
          <div key={f.key} className={fields.some((x) => x.col) ? (COL_SPAN[f.col] || 'sm:col-span-12') : (f.type === 'textarea' ? 'sm:col-span-2' : '')}>
            <label className="label">
              {f.label}{f.required && <span className="text-danger-500"> *</span>}
            </label>
            {f.type === 'select' && (
              <select value={valores[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}
                className="select-base">
                {f.placeholder && <option value="">{f.placeholder}</option>}
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            {f.type === 'ref' && (
              <select value={valores[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}
                className="select-base">
                <option value="">Selecciona...</option>
                {(refOptions[f.key] || []).filter((o) => (f.refFilter ? f.refFilter(o) : true)).map((o) => (
                  <option key={o._id} value={o._id}>{o[f.refLabel || 'nombre_comercial'] || o.name}</option>
                ))}
              </select>
            )}
            {f.type === 'textarea' && (
              <textarea value={valores[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} rows={3}
                className="input-base" />
            )}
            {!['select', 'ref', 'textarea'].includes(f.type) && (
              <input
                type={f.type || 'text'} value={valores[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="input-base"
              />
            )}
            {errores[f.key] && <p className="text-xs text-danger-500 mt-1">{errores[f.key]}</p>}
          </div>
        ))}
      </form>
    </Modal>
  );
}
